import { Box, Skeleton, Typography } from "@mui/material";
import Flex from "../../../../../components/Flex";
import { KPI_CARDS, KPICardConfig } from "./appointment-report-constants";
import { CHART_TYPOGRAPHY } from "../../../../../components/app/provider/patient/chart";

interface AppointmentReportKPICardsProps {
  data: Record<string, any> | undefined;
  isLoading: boolean;
}

const VALUE_BOX_STYLE = {
  backgroundColor: "#CDF4F8",
  border: "1px solid #AEE7ED",
  borderRadius: "4px",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
};

const KPICard = ({
  config,
  data,
  isLoading,
}: {
  config: KPICardConfig;
  data: Record<string, any> | undefined;
  isLoading: boolean;
}) => {
  const value = data?.[config.valueKey] ?? 0;

  return (
    <Box
      sx={{
        border: "1px solid #E7E7E7",
        borderRadius: "4px",
        padding: "8px 12px",
        flex: "1 1 0",
        minWidth: 200,
        backgroundColor: "#FFFFFF",
      }}
    >
      {isLoading ? (
        <Flex justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Skeleton width="80%" height={18} />
            <Skeleton width="60%" height={14} sx={{ mt: 1 }} />
          </Box>
          <Skeleton width={50} height={35} variant="rounded" />
        </Flex>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" gap="6px">
          <Box flex={1}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={500}
              color="#373D41"
              noWrap
              lineHeight={1.2}
            >
              {config.label}
            </Typography>
          </Box>

          <Box sx={VALUE_BOX_STYLE}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={600}
              color="#373D41"
            >
              {config.format === "number" ? value?.toLocaleString() : value}
            </Typography>
            {config.format === "percent" && (
              <Typography
                fontSize={CHART_TYPOGRAPHY.body5.fontSize}
                fontWeight={500}
                color="#373D41"
              >
                %
              </Typography>
            )}
          </Box>
        </Flex>
      )}
    </Box>
  );
};

/** Cancellation Rate card with Patient/Provider progress bar */
const CancellationRateCard = ({
  data,
  isLoading,
}: {
  data: Record<string, any> | undefined;
  isLoading: boolean;
}) => {
  const value = data?.cancellationRate ?? 0;
  // const patientRate = data?.patientCancellationRate ?? 0;
  // const providerRate = data?.providerCancellationRate ?? 0;
  // // const hasBreakdown = patientRate > 0 || providerRate > 0;
  // const total = patientRate + providerRate || 1;
  // const patientPercent = hasBreakdown ? (patientRate / total) * 100 : 50;
  // const providerPercent = hasBreakdown ? (providerRate / total) * 100 : 50;

  return (
    <Box
      sx={{
        border: "1px solid #E7E7E7",
        borderRadius: "4px",
        padding: "8px 12px",
        flex: "1 1 0",
        minWidth: 200,
        backgroundColor: "#FFFFFF",
      }}
    >
      {isLoading ? (
        <Flex justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Skeleton width="80%" height={18} />
            <Skeleton width="100%" height={6} sx={{ mt: 1 }} />
            <Skeleton width="60%" height={12} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton width={50} height={35} variant="rounded" />
        </Flex>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" gap="16px">
          <Box flex={1}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={500}
              color="#373D41"
              noWrap
              lineHeight={1.2}
            >
              Cancellation Rate
            </Typography>

            {/* Global cancellation rate strip */}
            <Box
              mt={0.75}
              sx={{
                height: 4,
                borderRadius: "8px",
                backgroundColor: "#E0E0E0",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${value}%`,
                  height: "100%",
                  backgroundColor: "#7048C2",
                  borderRadius: "8px",
                }}
              />
            </Box>
            {/* individual breakdown bars */}

            {/* <Flex
              mt={0.75}
              gap="2px"
              height={4}
              borderRadius="8px"
              overflow="hidden"
            >
              <Box
                sx={{
                  width: `${patientPercent}%`,
                  backgroundColor: "#0B406E",
                  borderRadius: "4px 0 0 4px",
                }}
              />
              <Box
                sx={{
                  width: `${providerPercent}%`,
                  backgroundColor: "#7048C2",
                  borderRadius: "0 4px 4px 0",
                }}
              />
            </Flex>

            <Flex alignItems="center" gap="12px" mt={0.5}>
              <Flex alignItems="center" gap="4px">
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "#0B406E",
                  }}
                />
                <Typography fontSize={10} color="#596063">
                  Patient
                </Typography>
              </Flex>
              <Flex alignItems="center" gap="4px">
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "#7048C2",
                  }}
                />
                <Typography fontSize={10} color="#596063">
                  Provider
                </Typography>
              </Flex>
            </Flex> */}
          </Box>

          <Box sx={VALUE_BOX_STYLE}>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={600}
              color="#373D41"
            >
              {value}
            </Typography>
            <Typography
              fontSize={CHART_TYPOGRAPHY.body5.fontSize}
              fontWeight={500}
              color="#373D41"
            >
              %
            </Typography>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

const AppointmentReportKPICards = ({
  data,
  isLoading,
}: AppointmentReportKPICardsProps) => {
  const standardCards = KPI_CARDS.filter((c) => c.key !== "cancellation");

  return (
    <Flex gap="12px" flexWrap="wrap">
      {standardCards.map((card) => (
        <KPICard
          key={card.key}
          config={card}
          data={data}
          isLoading={isLoading}
        />
      ))}
      <CancellationRateCard data={data} isLoading={isLoading} />
    </Flex>
  );
};

export default AppointmentReportKPICards;
